import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatNumber } from '@/utils/number';
import type { EntrySheet } from '../types/productRequisition.types';
import { cellKey, classColumnLabel, type EntryDraft } from '../utils/sheetDrafts';

interface StudentEntryGridProps {
  sheet: EntrySheet;
  draft: EntryDraft;
  onChange: (key: string, value: string) => void;
}

export function StudentEntryGrid({ sheet, draft, onChange }: StudentEntryGridProps) {
  const rowTotal = (instituteId: number): number =>
    sheet.classCategories.reduce((sum, category) => {
      const value = draft[cellKey(instituteId, category.classId, category.classGroup)] ?? '';
      const parsed = Number(value);
      return sum + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);

  return (
    <div className="overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">SL</TableHead>
            <TableHead className="min-w-56">Institute</TableHead>
            <TableHead>Thana</TableHead>
            {sheet.classCategories.map((category) => (
              <TableHead key={category.id} className="w-28 text-center">
                {classColumnLabel(category)}
              </TableHead>
            ))}
            <TableHead className="w-24 text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sheet.institutes.map((institute, index) => (
            <TableRow key={institute.instituteId}>
              <TableCell className="tabular-nums">{index + 1}</TableCell>
              <TableCell className="font-medium">{institute.instituteName}</TableCell>
              <TableCell>{institute.thanaName ?? EMPTY_VALUE}</TableCell>

              {sheet.classCategories.map((category) => {
                const key = cellKey(institute.instituteId, category.classId, category.classGroup);
                return (
                  <TableCell key={category.id}>
                    <Input
                      type="number"
                      min={0}
                      disabled={!sheet.isOpen}
                      aria-label={`${classColumnLabel(category)} students at ${institute.instituteName}`}
                      value={draft[key] ?? ''}
                      onChange={(event) => onChange(key, event.target.value)}
                    />
                  </TableCell>
                );
              })}

              <TableCell className="text-right font-medium tabular-nums">
                {formatNumber(rowTotal(institute.instituteId))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
